import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'

import { SelectField } from '@/components/ui/controls'
import { DataTable } from '@/components/ui/data-table'

import { StatusBadge, Notice, DateRangeField } from '../products/PageHelpers'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import dayjs from 'dayjs'
import { getOrders, exportOrders } from '../../services/order'
import { formatTimestamp } from '../../utils/format'
import {
  formatAmount,
  formatDisplayExchangeRate,
} from '../../utils/orderDisplay'
import {
  orderDisplayStatus,
  orderStatusColors,
  orderStatusLabels,
  shouldShowOrderFees,
} from '../../utils/orderDisplayStatus'
import type { Order, Pagination } from '../../types'

const TEXT_FILTER_DEBOUNCE_MS = 300

const paymentMethodLabels: Record<string, string> = {
  CREDIT_CARD: '信用卡',
  APPLE_PAY: 'Apple Pay',
  GOOGLE_PAY: 'Google Pay',
  PAYPAL: 'PayPal',
  WECHAT_PAY: '微信支付',
  ALIPAY: '支付寶',
  ALIPAY_HK: 'AlipayHK',
  BOOST: 'Boost',
  TOUCH_N_GO_EWALLET: "Touch 'n Go",
  LINE_PAY: 'LINE Pay',
  KREDIVO: 'Kredivo',
  GCASH: 'GCash',
  JKOPAY: 'JKOPay',
  PAYPAY: 'PayPay',
  MERPAY: 'Merpay',
  AUPAY: 'au PAY',
  MIR: 'Mir',
  SBP: 'SBP',
  T_PAY: 'T-Pay',
  SBER_PAY: 'Sber Pay',
  YOO_MONEY: 'YooMoney',
  KAKAO_PAY: 'Kakao Pay',
  NAVER_PAY: 'Naver Pay',
  TOSS_PAY: 'Toss Pay',
  PAYCO: 'PAYCO',
  SAMSUNG_PAY: 'Samsung Pay',
  T_MONEY: 'T-money',
  PAYNOW: 'PayNow',
  QRIS: 'QRIS',
  OVO: 'OVO SNAP',
  DANA: 'DANA',
  SHOPEE_PAY: 'ShopeePay',
  COINS_PH: 'Coins.ph',
  GO_PAY: 'GoPay',
  GRAB_PAY: 'GrabPay',
  MAYA: 'Maya',
  MOMO: 'MoMo',
  TRUE_MONEY: 'TrueMoney',
  ZALO_PAY: 'ZaloPay',
  PROMPT_PAY: 'PromptPay',
  VIET_QR: 'VietQR',
  DUITNOW: 'DuitNow',
  VIRTUAL_ACCOUNT: 'Virtual Account / 銀行轉賬',
  CRYPTO: '加密貨幣',
  USDT_DIRECT: 'USDT Direct',
}

export default function OrderList() {
  const [orders, setOrders] = useState<Order[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    size: 20,
    total: 0,
    totalPages: 0,
  })
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<string | undefined>()
  const [status, setStatus] = useState<string | undefined>()
  const [orderIdInput, setOrderIdInput] = useState('')
  const [orderId, setOrderId] = useState<string | undefined>()
  const [checkoutIdInput, setCheckoutIdInput] = useState('')
  const [checkoutId, setCheckoutId] = useState<string | undefined>()
  const [customerEmailInput, setCustomerEmailInput] = useState('')
  const [customerEmail, setCustomerEmail] = useState<string | undefined>()
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([null, null])
  const [exporting, setExporting] = useState(false)
  const [pageSize, setPageSize] = useState(20)
  const navigate = useNavigate()

  const fetchOrders = (page: number = 1, size: number = pageSize) => {
    setLoading(true)
    setLoadError(false)
    const params: Parameters<typeof getOrders>[0] = {
      page,
      size,
      status,
      paymentMethod,
      orderId,
      checkoutId,
      customerEmail,
    }
    if (dateRange[0] && dateRange[1]) {
      params.startDate = dateRange[0].valueOf()
      params.endDate = dateRange[1].endOf('day').valueOf()
    }
    getOrders(params)
      .then((res) => {
        setOrders(res.data.items)
        setPagination(res.data.pagination)
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }

  const handleExport = async () => {
    if (!dateRange[0] || !dateRange[1]) return
    setExporting(true)
    try {
      const blob = await exportOrders(
        dateRange[0].valueOf(),
        dateRange[1].endOf('day').valueOf(),
        { orderId, checkoutId, customerEmail, status, paymentMethod },
      )
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `orders_${dateRange[0].format('YYYY-MM-DD')}_${dateRange[1].format('YYYY-MM-DD')}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } finally {
      setExporting(false)
    }
  }

  const applyOrderId = (value: string) => {
    setOrderId(value.trim() || undefined)
  }

  const applyCheckoutId = (value: string) => {
    setCheckoutId(value.trim() || undefined)
  }

  const applyCustomerEmail = (value: string) => {
    setCustomerEmail(value.trim() || undefined)
  }

  const handleReset = () => {
    setOrderIdInput('')
    setOrderId(undefined)
    setCheckoutIdInput('')
    setCheckoutId(undefined)
    setCustomerEmailInput('')
    setCustomerEmail(undefined)
    setStatus(undefined)
    setPaymentMethod(undefined)
    setDateRange([null, null])
  }

  useEffect(() => {
    const value = orderIdInput.trim()
    if (!value) {
      setOrderId(undefined)
      return
    }

    const timer = window.setTimeout(() => {
      setOrderId(value)
    }, TEXT_FILTER_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [orderIdInput])

  useEffect(() => {
    const value = checkoutIdInput.trim()
    if (!value) {
      setCheckoutId(undefined)
      return
    }

    const timer = window.setTimeout(() => {
      setCheckoutId(value)
    }, TEXT_FILTER_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [checkoutIdInput])

  useEffect(() => {
    const value = customerEmailInput.trim()
    if (!value) {
      setCustomerEmail(undefined)
      return
    }

    const timer = window.setTimeout(() => {
      setCustomerEmail(value)
    }, TEXT_FILTER_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [customerEmailInput])

  useEffect(() => {
    fetchOrders(1)
  }, [status, paymentMethod, dateRange, orderId, checkoutId, customerEmail])

  const computeFee = (record: Order) =>
    record.platformFeeUsd
      ? `${formatAmount(record.platformFeeUsd)} USD`
      : `${formatAmount(record.platformFee)} ${record.currency}`

  const columns = [
    {
      key: 'id',
      header: '订单号 / Checkout ID',
      render: (row: Order) => {
        const record = row
        return (
          <div>
            <div>{record.id}</div>
            <span
              style={{ display: 'block' }}
              className="text-muted-foreground"
            >
              {record.checkoutSessionId || '-'}
            </span>
          </div>
        )
      },
    },
    {
      key: 'productSnapshot.name',
      header: '產品名稱',
      render: (row: Order) => {
        const record = row
        return record.productSnapshot?.name || '-'
      },
    },
    {
      key: 'customerEmail',
      header: '客戶郵箱',
      render: (row: Order) => {
        const email = row['customerEmail']
        return email || '-'
      },
    },
    {
      key: 'paymentMethod',
      header: '支付方式',
      render: (row: Order) => {
        const method = row['paymentMethod']
        return paymentMethodLabels[method] || method
      },
    },
    {
      key: 'amount',
      header: '訂單金額',
      className: 'text-right tabular-nums',
      render: (row: Order) => {
        const record = row
        return `${formatAmount(record.amount)} ${record.currency}`
      },
    },
    {
      key: 'exchangeRate',
      header: '匯率',
      className: 'text-right tabular-nums',
      render: (row: Order) => {
        const record = row
        return formatDisplayExchangeRate(record.currency, record.exchangeRate)
      },
    },
    {
      key: 'fee',
      header: '手續費',
      className: 'text-right tabular-nums',
      render: (row: Order) => {
        const record = row
        return shouldShowOrderFees(record.status) ? computeFee(record) : '-'
      },
    },
    {
      key: 'status',
      header: '狀態',
      render: (row: Order) => {
        const order = row
        const displayStatus = orderDisplayStatus(
          order.status,
          order.refundStatus,
        )
        return (
          <StatusBadge color={orderStatusColors[displayStatus]}>
            {orderStatusLabels[displayStatus] || displayStatus}
          </StatusBadge>
        )
      },
    },
    {
      key: 'createdAt',
      header: '創建時間',
      className: 'text-xs text-muted-foreground',
      render: (row: Order) => {
        const ts = row['createdAt']
        return formatTimestamp(ts)
      },
    },
  ]

  return (
    <div>
      <h2
        style={{ marginBottom: 16 }}
        className="mb-4 text-xl font-semibold tracking-tight"
      >
        訂單管理
      </h2>
      {loadError && (
        <Notice
          tone={'error'}
          title={'訂單載入失敗'}
          action={
            <Button
              onClick={() => fetchOrders(pagination.page)}
              variant={'outline'}
              type="button"
              size={'sm'}
            >
              重新載入
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}
      <Card>
        <div className="space-y-4 px-5">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              marginBottom: 16,
              width: '100%',
            }}
          >
            <Input
              placeholder={'輸入訂單 ID'}
              style={{ flex: '1 1 280px', maxWidth: 320, minWidth: 0 }}
              value={orderIdInput}
              onChange={(event) => setOrderIdInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') (() => applyOrderId(orderIdInput))()
              }}
            />
            <Input
              placeholder={'輸入 Checkout ID'}
              style={{ flex: '1 1 280px', maxWidth: 320, minWidth: 0 }}
              value={checkoutIdInput}
              onChange={(event) => setCheckoutIdInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter')
                  (() => applyCheckoutId(checkoutIdInput))()
              }}
            />
            <Input
              placeholder={'輸入客戶郵箱'}
              style={{ flex: '1 1 280px', maxWidth: 320, minWidth: 0 }}
              value={customerEmailInput}
              onChange={(event) => setCustomerEmailInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter')
                  (() => applyCustomerEmail(customerEmailInput))()
              }}
            />
            <div style={{ width: 150, maxWidth: '100%' }}>
              <SelectField
                aria-label="訂單狀態"
                options={[
                  { value: '', label: '全部' },
                  ...[
                    { value: 'PENDING', label: '待支付' },
                    { value: 'PAID', label: '已支付' },
                    { value: 'SETTLED', label: '已結算' },
                    { value: 'REFUNDED', label: '已退款' },
                    { value: 'DISPUTED', label: '爭議中' },
                    { value: 'CLOSED', label: orderStatusLabels.CLOSED },
                    { value: 'FAILED', label: orderStatusLabels.FAILED },
                  ],
                ]}
                placeholder={'訂單狀態'}
                value={status}
                onValueChange={(value) => setStatus(value || undefined)}
              />
            </div>
            <div style={{ width: 180, maxWidth: '100%' }}>
              <SelectField
                aria-label="支付方式"
                options={[
                  { value: '', label: '全部支付方式' },
                  ...Object.entries(paymentMethodLabels).map(([value, label]) => ({ value, label })),
                ]}
                value={paymentMethod}
                onValueChange={(value) => setPaymentMethod(value || undefined)}
              />
            </div>
            <DateRangeField
              value={dateRange}
              onChange={(dates) => setDateRange(dates || [null, null])}
            />
            <Button
              onClick={handleExport}
              variant={'outline'}
              disabled={Boolean(exporting || !dateRange[0] || !dateRange[1])}
              type="button"
            >
              導出
            </Button>
            <Button
              onClick={handleReset}
              aria-label={'重置'}
              variant={'outline'}
              type="button"
            >
              重置
            </Button>
          </div>
          <DataTable
            rowKey={'id'}
            loading={loading}
            columns={columns}
            data={orders}
            onRow={(order) => ({
              tabIndex: 0,
              title: '點擊查看訂單詳情',
              className: 'cursor-pointer hover:bg-accent/50 focus-visible:outline-2 focus-visible:outline-primary focus-visible:-outline-offset-2',
              onClick: (event) => {
                if (event.defaultPrevented || window.getSelection()?.toString()) return
                if (event.target instanceof Element && event.target.closest('a, button, input, select, textarea, [role="button"], [role="checkbox"], [contenteditable="true"]')) return
                navigate(`/dashboard/orders/${order.id}`)
              },
              onKeyDown: (event) => {
                if (event.target !== event.currentTarget) return
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  navigate(`/dashboard/orders/${order.id}`)
                }
              },
            })}
            pagination={{
              page: pagination.page,
              size: pagination.size,
              total: pagination.total,
              onChange: (page, size) => {
                if (size !== pageSize) {
                  setPageSize(size)
                  fetchOrders(1, size)
                } else {
                  fetchOrders(page, size)
                }
              },
            }}
          />
        </div>
      </Card>
    </div>
  )
}
